import { EnvironmentProviders, Injectable, inject } from '@angular/core';
import { buildStepToWorkflowMap, provideWorkflowTracking, TrackWorkflowService } from '@aiwyn/shared';
import {
  PaymentPlanEventEventTypeMap,
  PaymentPlanWorkflowEvent,
  PaymentPlanWorkflowEventMetaDataMap,
} from './payment-plan-workflow.models';
import { PaymentsWorkflowType } from '../payments-workflow.models';

/**
 * Per-container tracker for Flow 8 (Payment Plans). Narrows `TrackWorkflowService` to the
 * payment-plan step enum + metadata map and exposes a small operation-duration helper so
 * SUBMITTED → SUCCEEDED / FAILED triads can stamp `operationDurationMs` without each caller
 * juggling its own `performance.now()` baseline.
 *
 * Per-container scope (`@Injectable()`, no `providedIn`). Wire via `providers: [PaymentPlanTrackService]`
 * on the host container so concurrently-mounted hosts don't share operation timers.
 */
@Injectable()
export class PaymentPlanTrackService {
  private readonly tracker = inject(TrackWorkflowService) as TrackWorkflowService<PaymentPlanWorkflowEvent, PaymentPlanWorkflowEventMetaDataMap>;
  private readonly operationStarts = new Map<string, number>();

  trackWorkflow<E extends PaymentPlanWorkflowEvent>(step: E, metadata?: PaymentPlanWorkflowEventMetaDataMap[E]): void {
    this.tracker.trackWorkflow(step, metadata);
  }

  startOperation(key: string): void {
    this.operationStarts.set(key, performance.now());
  }

  endOperation(key: string): number {
    const start = this.operationStarts.get(key);
    if (start === undefined) {
      return 0;
    }
    this.operationStarts.delete(key);
    return performance.now() - start;
  }
}

/**
 * Register the payment-plan workflow taxonomy with `TrackWorkflowService`. Currently the
 * global `providePaymentsWorkflowTracking()` covers all six payments workflows at app bootstrap;
 * this narrower provider is exported for completeness and to support test beds that need only
 * the payment-plan slice.
 */
export function providePaymentPlanWorkflowTracking(): EnvironmentProviders {
  return provideWorkflowTracking<PaymentPlanWorkflowEvent, PaymentPlanWorkflowEventMetaDataMap>({
    stepToWorkflow: buildStepToWorkflowMap({ [PaymentsWorkflowType.PAYMENT_PLAN]: PaymentPlanWorkflowEvent }),
    stepToEventType: PaymentPlanEventEventTypeMap,
  });
}
